import { useEffect, useState, FormEvent } from 'react';
import { Plus, Pencil, Trash2, Check, X, AlertCircle, ShieldCheck } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Permission } from '@/types';

type FormState = { name: string; label: string; description: string };
const EMPTY: FormState = { name: '', label: '', description: '' };

export function Permissions() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  // Add form
  const [adding, setAdding]       = useState(false);
  const [addForm, setAddForm]     = useState<FormState>(EMPTY);
  const [addError, setAddError]   = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);

  // Edit state
  const [editId, setEditId]         = useState<number | null>(null);
  const [editForm, setEditForm]     = useState<Omit<FormState, 'name'>>({ label: '', description: '' });
  const [editError, setEditError]   = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  const [deleting, setDeleting]     = useState(false);

  async function load() {
    setError(null);
    try {
      setPermissions(await api.listPermissions());
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load permissions.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // ── Add ──

  async function submitAdd(e: FormEvent) {
    e.preventDefault();
    setAddError(null);
    setAddSaving(true);
    try {
      const created = await api.createPermission(addForm);
      setPermissions((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setAddForm(EMPTY);
      setAdding(false);
    } catch (e) {
      setAddError(e instanceof ApiClientError ? e.message : 'Failed to create permission.');
    } finally {
      setAddSaving(false);
    }
  }

  // ── Edit ──

  function startEdit(p: Permission) {
    setEditId(p.id);
    setEditForm({ label: p.label, description: p.description });
    setEditError(null);
  }

  async function submitEdit(e: FormEvent) {
    e.preventDefault();
    if (editId === null) return;
    setEditError(null);
    setEditSaving(true);
    try {
      const updated = await api.updatePermission(editId, editForm);
      setPermissions((prev) => prev.map((p) => (p.id === editId ? updated : p)));
      setEditId(null);
    } catch (e) {
      setEditError(e instanceof ApiClientError ? e.message : 'Failed to update.');
    } finally {
      setEditSaving(false);
    }
  }

  // ── Delete ──

  async function confirmDelete() {
    if (deleteId === null) return;
    setDeleting(true);
    try {
      await api.deletePermission(deleteId);
      setPermissions((prev) => prev.filter((p) => p.id !== deleteId));
      setDeleteId(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to delete.');
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-4xl">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Permissions</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Define the RBAC permission keys used in workflows. Workflow creators pick from this list.
          </p>
        </div>
        {!adding && (
          <button onClick={() => { setAdding(true); setAddForm(EMPTY); setAddError(null); }} className="btn-primary self-start sm:self-auto">
            <Plus size={16} className="mr-2" /> Add permission
          </button>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <form
          onSubmit={submitAdd}
          className="bg-white dark:bg-gray-800 border border-brand-200 dark:border-brand-700 rounded-xl p-5 mb-6 space-y-4"
        >
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">New permission</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Permission key <span className="text-red-500">*</span></label>
              <input
                className="input font-mono"
                placeholder="patient.create"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                autoFocus
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Lowercase, dots/underscores allowed. This is what ProMed RBAC uses.
              </p>
            </div>
            <div>
              <label className="label">Label</label>
              <input
                className="input"
                placeholder="Create Patient"
                value={addForm.label}
                onChange={(e) => setAddForm((f) => ({ ...f, label: e.target.value }))}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Human-readable name shown in the workflow editor.
              </p>
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <input
              className="input"
              placeholder="Allows creating new patient records"
              value={addForm.description}
              onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          {addError && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle size={14} /> {addError}
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={addSaving} className="btn-primary">
              <Plus size={15} className="mr-1.5" />
              {addSaving ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => setAdding(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-400 text-sm mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Loading */}
      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">Loading…</div>}

      {/* Empty */}
      {!loading && permissions.length === 0 && !adding && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-16 text-center">
          <ShieldCheck size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No permissions defined</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Add permission keys so workflow creators can pick from a standardised list.
          </p>
          <button onClick={() => setAdding(true)} className="btn-primary">
            <Plus size={15} className="mr-1.5" /> Add first permission
          </button>
        </div>
      )}

      {/* Delete confirmation overlay */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Delete permission?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Workflows that use <code className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded">
                {permissions.find((p) => p.id === deleteId)?.name}
              </code> will keep their value but it will no longer appear in the dropdown.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteId(null)} className="btn-secondary" disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <Trash2 size={14} /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && permissions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <th className="px-5 py-3 text-left">Permission key</th>
                <th className="px-5 py-3 text-left hidden sm:table-cell">Label</th>
                <th className="px-5 py-3 text-left hidden md:table-cell">Description</th>
                <th className="px-5 py-3 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
              {permissions.map((p) => (
                editId === p.id ? (
                  // ── Inline edit row ──
                  <tr key={p.id} className="bg-brand-50 dark:bg-brand-900/10">
                    <td className="px-5 py-3">
                      <span className="font-mono text-sm text-gray-900 dark:text-white">{p.name}</span>
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell" colSpan={2}>
                      <form onSubmit={submitEdit} className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <input
                            className="input text-sm flex-1"
                            placeholder="Label"
                            value={editForm.label}
                            onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
                            autoFocus
                          />
                          <input
                            className="input text-sm flex-1"
                            placeholder="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                          />
                        </div>
                        {editError && (
                          <span className="text-xs text-red-600 dark:text-red-400">{editError}</span>
                        )}
                      </form>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={submitEdit}
                          disabled={editSaving}
                          className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50 transition-colors"
                          title="Save"
                        >
                          <Check size={15} />
                        </button>
                        <button
                          onClick={() => setEditId(null)}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Cancel"
                        >
                          <X size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // ── Normal row ──
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="font-mono text-gray-900 dark:text-white">{p.name}</span>
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell text-gray-700 dark:text-gray-300">
                      {p.label || <span className="text-gray-400 dark:text-gray-500 italic">—</span>}
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-gray-500 dark:text-gray-400 text-xs">
                      {p.description || <span className="italic">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(p)}
                          className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteId(p.id)}
                          className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
